import { createContext, useContext, useEffect, useState } from "react";
import { auth, db } from "../firebaseConfig";
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { useLoading } from "./LoadingContext";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const { setIsLoading } = useLoading();

  useEffect(() => {
    setIsLoading(true);
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        const userRef = doc(db, "users", authUser.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          setUser(authUser);
          setRole(userDoc.data().role);
        } else {
          const defaultRole = "customer";
          await setDoc(userRef, { role: defaultRole, email: authUser.email });
          setUser(authUser);
          setRole(defaultRole);
        }
      } else {
        setUser(null);
        setRole(null);
      }
      setLoading(false);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [setIsLoading]);

  const login = (email, password) => {
    return new Promise((resolve, reject) => {
      setIsLoading(true);
      signInWithEmailAndPassword(auth, email, password)
        .then((userCredential) => {
          // Wait for onAuthStateChanged to update the user state
          const unsubscribe = onAuthStateChanged(auth, (authUser) => {
            if (authUser) {
              // User is authenticated and state is updated
              unsubscribe(); // Clean up the listener
              setIsLoading(false);
              resolve(authUser);
            }
          });
        })
        .catch((error) => {
          setIsLoading(false);
          reject(error);
        });
    });
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await signOut(auth);
      setUser(null);
      setRole(null);
    } finally {
      setIsLoading(false);
    }
  };

  const elevateUser = async (uid, newRole) => {
    try {
      const userRef = doc(db, "users", uid);
      await updateDoc(userRef, { role: newRole });

      if (user && user.uid === uid) {
        setRole(newRole);
      }
      return { success: true, message: `User ${uid} elevated to ${newRole}` };
    } catch (error) {
      console.error("Error elevating user:", error);
      return { success: false, message: "Failed to elevate user: " + error.message };
    }
  };

  return (
    <AuthContext.Provider value={{ user, role, login, logout, elevateUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);